// Live activity feed vocabulary. Maps parsed NDJSON console events (see
// utils/serialEvents.js) to structured feed rows for the workbench's Live tab:
// a title, an optional detail line, an optional right-aligned value, a filter
// group, and a tone for the icon tint. Pure functions, no React.
//
// Anything not worth an operator's glance returns null: legend/status lines
// (they feed devCfg, not the feed), per-second flow ticks, and 100 Hz
// calibration samples.

import { t } from "i18n";
import { describeEvent } from "./serialEvents";

// Filter chips shown above the feed. `group` on each row matches an id here.
// `label` is a getter so it is translated at read time, not at module load.
const feedFilter = (id) => ({
  id,
  get label() {
    return t(`utils.liveFeed.filters.${id}`);
  },
});
export const FEED_FILTERS = [
  feedFilter("all"),
  feedFilter("impact"),
  feedFilter("session"),
  feedFilter("system"),
];

// Event types that never make the feed. The flow ticks (countdown/recording/
// progress/collecting) repeat up to once a second and belong to their flow
// UIs; `load` is the 38-sample waveform blob; `live` never reaches the store.
const SKIPPED = new Set([
  "status",
  "led",
  "id",
  "live",
  "ack",
  "countdown",
  "recording",
  "progress",
  "collecting",
  "cal_status",
]);

const mgToG = (mg) => (typeof mg === "number" ? mg / 1000 : null);

export const fmtG = (mg) => {
  const v = mgToG(mg);
  if (v == null) return null;
  return `${v >= 100 ? v.toFixed(0) : v.toFixed(1)}g`;
};

const words = (s) => String(s || "").replace(/_/g, " ");

const SESSION_EV = {
  arming: (ev) => ({
    title: t("utils.liveFeed.session.arming"),
    detail:
      ev.grace_s != null
        ? t("utils.liveFeed.session.donningGrace", { seconds: ev.grace_s })
        : null,
    tone: "warn",
  }),
  recording: () => ({
    title: t("utils.liveFeed.session.recordingStarted"),
    tone: "ok",
  }),
  ending: (ev) => ({
    title: t("utils.liveFeed.session.ending"),
    detail: ev.reason ? words(ev.reason) : null,
  }),
  countdown: (ev) => ({
    title: t("utils.liveFeed.session.startsSoon"),
    detail: ev.starts_in
      ? t("utils.liveFeed.session.inTime", { time: ev.starts_in })
      : null,
    tone: "warn",
  }),
  carryover: (ev) => ({
    title: t("utils.liveFeed.session.carriedOver"),
    detail:
      ev.records != null
        ? t("utils.liveFeed.session.carryoverDetail", {
            records: t("utils.liveFeed.recordCount", { count: ev.records }),
            sessions:
              ev.sessions != null
                ? t("utils.liveFeed.session.earlierSessionCount", {
                    count: ev.sessions,
                  })
                : t("utils.liveFeed.session.earlierSessionCountUnknown"),
          })
        : null,
  }),
  config_window: () => ({
    title: t("utils.liveFeed.session.configWindow"),
    detail: t("utils.liveFeed.session.configWindowDetail"),
  }),
  sched_wait: (ev) => ({
    title: t("utils.liveFeed.session.waitingScheduled"),
    detail: ev.starts_in
      ? t("utils.liveFeed.session.startsIn", { time: ev.starts_in })
      : null,
    tone: "warn",
  }),
  sched_reached: () => ({
    title: t("utils.liveFeed.session.scheduledReached"),
    tone: "ok",
  }),
  auto_erase: () => ({ title: t("utils.liveFeed.session.storeAutoErased") }),
  hold_link: (ev) => ({
    // Grace elapsed but a BLE config link is open — the device is
    // deliberately waiting, not hung. Say who's holding it and for how long.
    title: t("utils.liveFeed.session.holdLink"),
    detail: [
      ev.assigned
        ? t("utils.liveFeed.session.hubMidHandshake")
        : t("utils.liveFeed.session.neverHandshake"),
      ev.starts_in_s != null
        ? t("utils.liveFeed.session.startsAnyway", { seconds: ev.starts_in_s })
        : null,
    ]
      .filter(Boolean)
      .join(" · "),
    tone: "warn",
  }),
  handshake_done: () => ({
    title: t("utils.liveFeed.session.handshakeDone"),
    tone: "ok",
  }),
};

/**
 * Map one parsed console event to a feed row, or null to skip it.
 * Returns { group, tone, title, detail, value } — group is a FEED_FILTERS id,
 * tone one of "accent" | "ok" | "warn" | "error" | "muted" (default neutral).
 */
export function feedItemFromEvent(ev) {
  if (!ev || typeof ev !== "object" || typeof ev.t !== "string") return null;
  if (SKIPPED.has(ev.t)) return null;

  switch (ev.t) {
    case "impact": {
      const parts = [
        ev.band === "low"
          ? t("utils.liveFeed.impact.lowBand")
          : t("utils.liveFeed.impact.highBand"),
        ev.id != null ? `#${ev.id}` : null,
        ev.dur_ms != null ? `${ev.dur_ms} ms` : null,
        // Peak rotation (fw v0.6.4+). Shown in dps at a glance; the detail card
        // carries rad/s too. Omitted entirely when no gyro sample reached the
        // event -- a blank is honest, "0 dps" would not be.
        ev.gyro_mdps != null && ev.gyro_ok !== false
          ? `${(ev.gyro_mdps / 1000).toFixed(0)} dps`
          : null,
      ].filter(Boolean);
      return {
        kind: "impact",
        group: "impact",
        tone: "accent",
        title:
          ev.band === "low"
            ? t("utils.liveFeed.impact.eventDetected")
            : t("utils.liveFeed.impact.impactDetected"),
        detail: parts.join(" · ") || null,
        value: fmtG(ev.peak_mg),
        // Dry-run gate verdict, surfaced as a chip on the row.
        gate: ev.gate || null,
      };
    }

    case "tx": {
      if (ev.pkt === "0x12") {
        const bits = [];
        if (ev.impacts != null)
          bits.push(t("utils.liveFeed.beacon.impacts", { count: ev.impacts }));
        if (ev.bcasts != null)
          bits.push(t("utils.liveFeed.beacon.bcasts", { count: ev.bcasts }));
        if (ev.batt_pct != null)
          bits.push(
            t(
              ev.flags & 1
                ? "utils.liveFeed.beacon.batteryCharging"
                : "utils.liveFeed.beacon.battery",
              { pct: ev.batt_pct }
            )
          );
        return {
          kind: "beacon",
          group: "system",
          tone: "muted",
          title: t("utils.liveFeed.beacon.title"),
          detail: bits.join(" · ") || null,
        };
      }
      if (ev.pkt === "0x10") {
        return {
          kind: "broadcast",
          group: "impact",
          tone: "muted",
          title: t("utils.liveFeed.impactBroadcast"),
          detail: [
            ev.id != null ? `#${ev.id}` : null,
            ev.tag && ev.tag !== "tx" ? ev.tag : null,
          ]
            .filter(Boolean)
            .join(" · "),
          value: fmtG(ev.mag_mg),
        };
      }
      if (ev.pkt === "0x11") {
        // Its own row, never folded into an impact anchor: snippet ids are
        // low-band EVENT ids, not impact ids, and the waveform is the point.
        return {
          kind: "waveform",
          group: "impact",
          tone: "info",
          title: t("utils.liveFeed.waveformBroadcast"),
          detail:
            ev.id != null
              ? `#${ev.id} · ${t("utils.liveFeed.snippet")}`
              : t("utils.liveFeed.snippet"),
          value: fmtG(ev.max_mg),
        };
      }
      return null;
    }

    case "time":
      // Only a SET is an event ("Sync clock" was pressed, or auto-sync ran).
      // Reports fire on every connect handshake — pure noise in a feed.
      if (ev.result !== "set") return null;
      return {
        kind: "clock",
        group: "system",
        tone: "ok",
        title: t("utils.liveFeed.clockSynced"),
        detail: ev.utc ? t("utils.liveFeed.utcTime", { time: ev.utc }) : null,
      };

    case "load":
      // Low-band waveform capture — the ANCHOR row for this event id. Its
      // 0x11 broadcast folds in as a "sent" chip (see foldFeedRows), and
      // ev.stored becomes the "saved" chip, mirroring the impact rows.
      return {
        kind: "waveform",
        group: "impact",
        tone: "info",
        title: t("utils.liveFeed.loadWaveform"),
        detail: [
          ev.id != null ? `#${ev.id}` : null,
          t("utils.liveFeed.loadWindow"),
          ev.continued ? t("utils.liveFeed.continued") : null,
        ]
          .filter(Boolean)
          .join(" · "),
        value: fmtG(ev.max_mg),
      };

    case "state":
      if (typeof ev.from !== "string" || typeof ev.to !== "string") return null;
      return {
        kind: "state",
        group: "session",
        tone: ev.to === "SESSION" ? "ok" : "default",
        title: `${ev.from} → ${ev.to}`,
      };

    case "substate":
      // The state event already narrates the reset; only real phases matter.
      if (!ev.sub || ev.sub === "none") return null;
      return {
        kind: "state",
        group: "session",
        tone: ev.sub === "recording" ? "ok" : "warn",
        title: words(ev.sub),
        detail: ev.state || null,
      };

    case "session": {
      const build = SESSION_EV[ev.ev];
      const base = build
        ? build(ev)
        : {
            title: words(ev.ev) || t("utils.liveFeed.session.fallbackTitle"),
            detail: null,
          };
      return { kind: "session", group: "session", tone: "default", ...base };
    }

    case "button":
      return {
        kind: "button",
        group: "session",
        tone: "default",
        title: t("utils.liveFeed.buttonTap", {
          press: t(`utils.liveFeed.press.${ev.press}`, {
            defaultValue: words(ev.press),
          }),
        }),
        detail: ev.action ? words(ev.action) : null,
      };

    case "store":
      return ev.ev === "save_failed"
        ? {
            kind: "store",
            group: "system",
            tone: "error",
            title: t("utils.liveFeed.recordSaveFailed"),
            detail:
              ev.impact_id != null
                ? t("utils.liveFeed.impactRef", { id: ev.impact_id })
                : null,
          }
        : {
            kind: "store",
            group: "system",
            tone: "muted",
            title: t("utils.liveFeed.recordSaved"),
            detail:
              ev.impact_id != null
                ? t("utils.liveFeed.impactRef", { id: ev.impact_id })
                : null,
          };

    case "warning":
      return {
        kind: "warning",
        group: "system",
        tone: "warn",
        title: words(ev.reason || ev.ev) || t("utils.liveFeed.warning"),
        detail:
          ev.impact_id != null
            ? t("utils.liveFeed.impactRef", { id: ev.impact_id })
            : null,
      };

    case "error":
      return {
        kind: "error",
        group: "system",
        tone: "error",
        title: words(ev.reason) || t("utils.liveFeed.deviceError"),
      };

    case "nack":
      return {
        kind: "error",
        group: "system",
        tone: "error",
        title: t("utils.liveFeed.refused", {
          cmd: ev.cmd || t("utils.liveFeed.command"),
        }),
        detail: [
          ev.reason ? words(ev.reason) : null,
          ev.state ? t("utils.liveFeed.inState", { state: ev.state }) : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };

    case "ble":
      return {
        kind: "ble",
        group: "system",
        tone: "muted",
        title: t("utils.liveFeed.ble", { event: words(ev.ev) }),
        detail: ev.char || null,
      };

    case "sync":
      // The erase pair is worth real copy — it's destructive and the USB
      // "Erase all records" flow watches for these (same events as the BLE
      // erase path).
      if (ev.ev === "erase") {
        return {
          kind: "store",
          group: "system",
          tone: "warn",
          title: t("utils.liveFeed.erasingStore"),
          detail: [
            ev.records != null
              ? t("utils.liveFeed.recordCount", { count: ev.records })
              : null,
            ev.sectors != null
              ? t("utils.liveFeed.sectors", { count: ev.sectors })
              : null,
            ev.reason ? t("utils.liveFeed.via", { reason: ev.reason }) : null,
          ]
            .filter(Boolean)
            .join(" · "),
        };
      }
      if (ev.ev === "erase_progress") {
        // Ten bucketed events per erase (v0.5.86) — they drive the hero's
        // progress bar, not feed rows.
        return null;
      }
      if (ev.ev === "erase_done") {
        return {
          kind: "store",
          group: "system",
          tone: "ok",
          title: t("utils.liveFeed.storeErased"),
          detail:
            ev.sectors != null
              ? t("utils.liveFeed.sectors", { count: ev.sectors })
              : null,
        };
      }
      return {
        kind: "sync",
        group: "system",
        tone: "muted",
        title: t("utils.liveFeed.sync", { event: words(ev.ev) }),
      };

    default: {
      // Rare / flow events (centrifuge phases, calibration results, …):
      // render honestly via the terminal's describer rather than dropping.
      const desc = describeEvent(ev);
      return desc
        ? { kind: "generic", group: "system", tone: "muted", title: desc }
        : null;
    }
  }
}

/** Peak magnitude in g for the hero's impact pulse strip, or null. */
export function impactPeakG(ev) {
  if (!ev || ev.t !== "impact") return null;
  return mgToG(ev.peak_mg);
}

// ── Feed row folding (impact grouping) ──────────────────────────────
// One physical hit produces three console events — the detection, the radio
// broadcast, and the flash save — which used to render as three feed rows and
// bury the signal. Fold them into ONE row per impact id: the detection is the
// anchor, and matching broadcast/store events set flags on it instead of
// adding rows.

export const FEED_CAP = 150;
const GROUP_WINDOW = 40; // rows to search back for the anchor (newest-first)

// Where a follow-up event attaches: the impact id it belongs to, plus the
// flag patch it contributes. Null = not a follow-up (renders as its own row).
function groupTarget(ev) {
  if (!ev) return null;
  // 0x10 impact broadcasts fold into their impact detection row; 0x11
  // waveform snippets fold into their LOAD capture row (a separate id space
  // — snippet ids are low-band event ids, never impact ids). The snippet
  // also carries the decimated samples, so it rides along as `txEv` for the
  // expanded row's graph.
  if (ev.t === "tx" && ev.pkt === "0x10" && ev.id != null) {
    return ev.tag && ev.tag !== "tx"
      ? { family: "impact", id: ev.id, patch: { retx: 1 } }
      : { family: "impact", id: ev.id, patch: { broadcast: true } };
  }
  if (ev.t === "tx" && ev.pkt === "0x11" && ev.id != null) {
    return { family: "wave", id: ev.id, patch: { broadcast: true }, txEv: ev };
  }
  if (ev.t === "store" && ev.impact_id != null) {
    return ev.ev === "save_failed"
      ? { family: "impact", id: ev.impact_id, patch: { saveFailed: true } }
      : { family: "impact", id: ev.impact_id, patch: { saved: true } };
  }
  return null;
}

/**
 * Fold a batch of parsed events (oldest → newest, each { ev, ts, key }) into
 * a newest-first row list. Pure: returns { rows, changed }. Impact detections
 * become anchor rows carrying `impactId` and `flags`; their broadcast/store
 * follow-ups merge into the anchor (searched within the last GROUP_WINDOW
 * rows). A follow-up with no anchor in reach still gets its own row, so
 * nothing is ever silently dropped.
 */
export function foldFeedRows(prevRows, entries) {
  let rows = prevRows;
  let changed = false;
  const mutate = () => {
    if (!changed) {
      rows = prevRows.slice();
      changed = true;
    }
  };

  for (const { ev, ts, key } of entries) {
    const target = groupTarget(ev);
    if (target) {
      const limit = Math.min(rows.length, GROUP_WINDOW);
      let idx = -1;
      for (let i = 0; i < limit; i++) {
        const r = rows[i];
        const hit =
          target.family === "impact"
            ? r.kind === "impact" && r.impactId === target.id
            : r.kind === "waveform" && r.waveId === target.id;
        if (hit) {
          idx = i;
          break;
        }
      }
      if (idx !== -1) {
        mutate();
        const prev = rows[idx];
        const flags = { ...(prev.flags || {}) };
        if (target.patch.retx) flags.retx = (flags.retx || 0) + 1;
        else Object.assign(flags, target.patch);
        rows[idx] = {
          ...prev,
          flags,
          ...(target.txEv ? { txEv: target.txEv } : {}),
        };
        continue;
      }
    }
    const item = feedItemFromEvent(ev);
    if (!item) continue;
    mutate();
    const isImpact = ev.t === "impact" && ev.id != null;
    const isWave = ev.t === "load" || (ev.t === "tx" && ev.pkt === "0x11");
    rows.unshift({
      id: key,
      ts,
      ...item,
      ev,
      impactId: isImpact ? ev.id : null,
      waveId: isWave && ev.id != null ? ev.id : null,
      flags:
        ev.t === "impact"
          ? {}
          : ev.t === "load"
          ? { ...(ev.stored ? { saved: true } : {}) }
          : undefined,
    });
  }

  if (changed && rows.length > FEED_CAP) rows = rows.slice(0, FEED_CAP);
  return { rows, changed };
}
